import { Controller } from "@hotwired/stimulus"
import { Modal } from 'bootstrap'

export default class extends Controller {
  static targets = [
    'stateSelect',
    'downloadButton',
    'modal',
    'emailForm',
    'nameInput',
    'emailInput',
    'submitButton'
  ]

  connect() {
    if (this.hasModalTarget)
      this.emailSignupModal = new Modal(this.modalTarget)
  }

  async downloadPdf(event) {
    event.preventDefault()

    const selectedState = this.stateSelectTarget.value
    if (!selectedState) {
      alert('Please select a state before downloading.')
      return
    }

    this.setDownloadButtonLoading(true)

    try {
      const fileName = `2024-oaa-roi-infographic-${selectedState}.pdf`
      const downloadUrl = `/pdfs/oaa_roi_infographics/${fileName}`

      const response = await fetch(downloadUrl, { method: 'HEAD' })

      if (response.ok) {
        window.open(downloadUrl, '_blank')

        this.trackDownload(selectedState)
        this.showFlashMessage('Thank you for downloading!')
        this.showEmailSignupModal()
      } else if (response.status === 404) {
        console.error('PDF not found:', downloadUrl)
        this.showFlashMessage('Sorry, the infographic for this state is not yet available. Please try again later.', 'error')
      } else {
        console.error('Download failed with status:', response.status)
        this.showFlashMessage('Sorry, there was an error downloading the file. Please try again.', 'error')
      }
    } catch (error) {
      console.error('Download error:', error)
      this.showFlashMessage('Sorry, there was an error downloading the file. Please try again.', 'error')
    } finally {
      this.setDownloadButtonLoading(false)
    }
  }

  setDownloadButtonLoading(isLoading) {
    const button = this.downloadButtonTarget

    if (isLoading) {
      button.disabled = true
      button.innerHTML = `
        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Downloading...
      `
    } else {
      button.disabled = false
      button.innerHTML = 'Download'
    }
  }

  showEmailSignupModal() {
    this.emailSignupModal.show()
  }

  async submitEmail(event) {
    event.preventDefault()

    const name = this.nameInputTarget.value.trim()
    const email = this.emailInputTarget.value.trim()

    if (!name || !email) {
      alert('Please fill in both name and email fields.')
      return
    }

    this.setSubmitButtonLoading(true)

    try {
      const response = await fetch('/oaa/email-signups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({
          name: name,
          email: email,
          selected_state: this.stateSelectTarget.value
        })
      })

      if (response.ok) {
        this.closeModal()
        this.showFlashMessage('Thank you for downloading and signing up for updates!')
        this.trackEmailSignup(name, email, this.stateSelectTarget.value)
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
    } catch (error) {
      console.error('Email signup error:', error)
      alert('Sorry, there was an error signing you up. Please try again.')
    } finally {
      this.setSubmitButtonLoading(false)
    }
  }

  setSubmitButtonLoading(isLoading) {
    const button = this.submitButtonTarget

    if (isLoading) {
      button.disabled = true
      button.innerHTML = `
        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Signing up...
      `
    } else {
      button.disabled = false
      button.innerHTML = 'Sign Me Up'
    }
  }

  closeModal() {
    this.emailSignupModal.hide()
  }

  showFlashMessage(message, type = 'success') {
    const flashContainer = document.querySelector('.flash-messages') || this.createFlashContainer()
    const alertType = type === 'error' ? 'alert-danger' : 'alert-success'

    const flashElement = document.createElement('div')
    flashElement.className = `alert ${alertType} alert-dismissible fade show`
    flashElement.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `

    flashContainer.appendChild(flashElement)

    setTimeout(() => {
      if (flashElement.parentNode) {
        flashElement.remove()
      }
    }, 5000)
  }

  createFlashContainer() {
    const container = document.createElement('div')
    container.className = 'flash-messages fixed-top p-3'
    container.style.zIndex = '9999'
    document.body.prepend(container)
    return container
  }

  trackDownload(state) {
    fetch('/oaa/analytics/track-download', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken()
      },
      body: JSON.stringify({
        state: state,
        user_agent: navigator.userAgent
      })
    }).catch(error => {
      console.warn('Analytics tracking failed:', error)
    })
  }

  trackEmailSignup(name, email, state) {
    fetch('/oaa/analytics/track-email-signup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken()
      },
      body: JSON.stringify({
        event: 'oaa_roi_email_signup',
        state: state,
        timestamp: new Date().toISOString()
      })
    }).catch(error => {
      console.warn('Analytics tracking failed:', error)
    })
  }

  getCSRFToken() {
    const metaTag = document.querySelector('meta[name="csrf-token"]')
    return metaTag ? metaTag.getAttribute('content') : ''
  }
}
